import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@repo/api-client";
import type { Course, CourseLesson, TeacherProfile } from "@repo/shared-types";

const ACCESS_COLORS: Record<string, string> = {
  free: "cap-pill cap-pill--green",
  member: "cap-pill cap-pill--indigo",
  paid: "cap-pill cap-pill--gold",
};

export default function CourseDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [course, setCourse] = useState<Course | null>(null);
  const [lessons, setLessons] = useState<CourseLesson[]>([]);
  const [teacherSlug, setTeacherSlug] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { course: c, lessons: l } = await api.getCourse(slug);
        setCourse(c);
        setLessons(l);
        const { teachers } = await api.listTeachers();
        const teacher = teachers.find((t: TeacherProfile) => t.id === c.teacher_id);
        setTeacherSlug(teacher?.slug ?? null);
      } catch {
        setError("Course not found.");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) return <div className="cap-page"><div className="container" style={{ padding: "6rem 0", textAlign: "center" }}>Loading…</div></div>;
  if (error || !course) return <div className="cap-page"><div className="container" style={{ padding: "6rem 0", textAlign: "center", color: "#b45309" }}>{error || "Course not found"}</div></div>;

  const hours = Math.floor(course.duration_total_minutes / 60);
  const mins = course.duration_total_minutes % 60;
  const durationStr = hours > 0 ? `${hours}h ${mins > 0 ? mins + "m" : ""}` : `${mins}m`;

  return (
    <div className="cap-page">
      <section className="cap-hero">
        <div className="container">
          <Link to="/courses" className="btn btn--ghost" style={{ marginBottom: "1rem" }}>← All courses</Link>
          <div className="cap-hero__shell cap-hero__shell--split">
            <div>
              <div className="cap-card__meta-row" style={{ marginBottom: "0.75rem" }}>
                <span className={ACCESS_COLORS[course.access]}>{course.access.toUpperCase()}{course.access === "paid" && course.price ? ` · $${course.price}` : ""}</span>
                <span className="cap-pill" style={{ background: "rgba(61, 43, 31, 0.06)", color: "var(--c-muted)" }}>{course.level}</span>
              </div>
              <h1 className="display-1 cap-hero__title">{course.title}</h1>
              {course.subtitle && <p className="cap-hero__subtitle" style={{ fontSize: "1.15rem" }}>{course.subtitle}</p>}
              <p className="cap-hero__subtitle">{course.description}</p>
              <div className="hero-actions">
                <button className="btn btn--gold">{course.access === "free" ? "Enroll for free" : course.access === "member" ? "Start learning" : "Purchase course"}</button>
                <Link to={teacherSlug ? `/teachers/${teacherSlug}` : "/teachers"} className="btn btn--outline">Meet the teacher</Link>
              </div>
            </div>

            <aside className="cap-hero__aside">
              <div className="cap-hero__card">
                <div className="cap-stat-grid">
                  <div className="cap-stat"><span className="cap-stat__value">{course.lesson_count}</span><span className="cap-stat__label">lessons</span></div>
                  <div className="cap-stat"><span className="cap-stat__value">{durationStr}</span><span className="cap-stat__label">total runtime</span></div>
                  <div className="cap-stat"><span className="cap-stat__value">{course.enrolled_count.toLocaleString()}</span><span className="cap-stat__label">enrolled</span></div>
                  <div className="cap-stat"><span className="cap-stat__value">{course.rating?.toFixed(1) ?? "4.9"}</span><span className="cap-stat__label">avg rating</span></div>
                </div>
              </div>
              <div className="cap-panel">
                <div className="cap-panel__body">
                  <h3 className="heading-md" style={{ marginBottom: "0.75rem" }}>About the teacher</h3>
                  <Link to={teacherSlug ? `/teachers/${teacherSlug}` : "/teachers"} className="cap-card__meta-row" style={{ textDecoration: "none" }}>
                    <div className="cap-pill cap-pill--indigo">🙏</div>
                    <div>
                      <div className="heading-md" style={{ fontSize: "1rem" }}>{course.teacher_name}</div>
                      <div className="text-muted body-sm">View profile →</div>
                    </div>
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="section section--surface-alt">
        <div className="container">
          <div className="cap-layout">
            <div style={{ display: "grid", gap: "1.25rem" }}>
              {course.what_you_learn.length > 0 && (
                <section className="cap-panel">
                  <div className="cap-panel__header"><h2 className="heading-lg">What you'll learn</h2></div>
                  <div className="cap-panel__body">
                    <div className="cap-list">
                      {course.what_you_learn.map((item, i) => (
                        <div key={i} className="cap-list__item" style={{ alignItems: "flex-start" }}>
                          <span className="cap-list__index">✓</span>
                          <span className="body-sm" style={{ color: "var(--c-ink-75)" }}>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              )}

              <section className="cap-panel">
                <div className="cap-panel__header"><h2 className="heading-lg">Course curriculum</h2></div>
                <div className="cap-panel__body">
                  <div className="cap-list">
                    {lessons.map((lesson, i) => (
                      <LessonRow key={lesson.id} lesson={lesson} index={i} />
                    ))}
                  </div>
                </div>
              </section>

              {course.tags.length > 0 && (
                <div className="cap-card__meta-row">
                  {course.tags.map((t) => <span key={t} className="cap-pill" style={{ background: "white", color: "var(--c-muted)", border: "1px solid var(--c-border)" }}>{t}</span>)}
                </div>
              )}
            </div>

            <aside className="cap-panel">
              <div className="cap-panel__header"><h2 className="heading-lg">Enroll</h2></div>
              <div className="cap-panel__body">
                <div className="display-3" style={{ marginBottom: "0.25rem" }}>{course.access === "free" ? "Free" : course.access === "member" ? "Member access" : `$${course.price}`}</div>
                {course.access === "member" && <p className="text-muted body-sm" style={{ marginBottom: "1rem" }}>Requires active membership</p>}
                <button className="btn btn--gold" style={{ width: "100%" }}>{course.access === "free" ? "Enroll for free" : course.access === "member" ? "Start learning" : "Purchase course"}</button>
                <div style={{ marginTop: "1rem" }} className="cap-list">
                  <div className="cap-list__item"><span className="cap-list__index">🎓</span><span className="body-sm">{course.lesson_count} structured lessons</span></div>
                  <div className="cap-list__item"><span className="cap-list__index">⏱</span><span className="body-sm">{durationStr} total content</span></div>
                  <div className="cap-list__item"><span className="cap-list__index">🔄</span><span className="body-sm">Lifetime access</span></div>
                  <div className="cap-list__item"><span className="cap-list__index">📱</span><span className="body-sm">Mobile + desktop</span></div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}

function LessonRow({ lesson, index }: { lesson: CourseLesson; index: number }) {
  const formatIcon: Record<string, string> = { video: "🎬", audio: "🎙", text: "📄", mixed: "✨" };
  return (
    <div className="cap-list__item">
      <span className="cap-list__index">
        {index + 1}
      </span>
      <span style={{ fontSize: "1.1rem" }}>{formatIcon[lesson.format] || "📝"}</span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <p className="heading-md" style={{ fontSize: "1rem" }}>{lesson.title}</p>
        {lesson.description && <p className="body-sm text-muted" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lesson.description}</p>}
      </div>
      <div className="cap-card__meta-row" style={{ flexShrink: 0 }}>
        <span className="body-sm text-muted">{lesson.duration_minutes}m</span>
        <span className={lesson.is_preview ? "cap-pill cap-pill--green" : "cap-pill cap-pill--gold"}>{lesson.is_preview ? "Preview" : "Locked"}</span>
      </div>
    </div>
  );
}
